import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import {getOne, create} from '../../common/BackendProvider';
import Tabs from '../../layout/Tabs';
import {Panel} from '../../layout/Panel';
import { StarInactive, StarActive } from '../../layout/images/Stars';
import { EsgTab } from './tabs/EsgTab';
import { RiskTab } from './tabs/RiskTab';
import { FinanceTab } from './tabs/FinanceTab';
import { OverviewTab } from './tabs/overview';
import { NewsTab } from './tabs/NewsTab';

const tabs = ["Summary", "ESG", "Financial", "Risk", "News Articles"]

const getCompanyLogoUrl = (company) => {
    if (company.logo_url) {
        return company.logo_url;
    }

    if (!company.website) {
        return null;
    }

    try {
        const domain = new URL(company.website).hostname;
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
        return null;
    }
}

const CompanyLogo = ({company}) => {
    const logoUrl = getCompanyLogoUrl(company);
    const symbol = company.symbol || "";

    return (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {logoUrl ? (
                <img
                    src={logoUrl}
                    alt={`${company.name || symbol} logo`}
                    className="h-10 w-10 object-contain"
                    onError={(event) => {
                        event.currentTarget.style.display = 'none';
                    }}
                />
            ) : (
                <span className="text-base font-semibold text-[#061b2f]">{symbol.slice(0, 2)}</span>
            )}
        </div>
    )
}

const CompanyProfilePanel = ({company}) => {
    const [open, setOpen] = useState(true);
    const detailRows = [
        ['Sector', company.sector?.name],
        ['Industry', company.industry?.name],
        ['Country', company.country?.name],
        ['Region', company.region],
        ['CEO', company.ceo],
        ['Employees', company.employees],
        ['Market Cap', company.market_cap],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');

    return (
        <Panel className="mb-4 border border-slate-200 shadow-sm">
            <button
                type="button"
                className="flex w-full items-center justify-between gap-4 text-left"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
            >
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Company profile</p>
                    <h2 className="mt-1 text-lg font-semibold text-slate-950">About {company.symbol}</h2>
                </div>
                <span className="rounded-md border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-600">
                    {open ? 'Hide' : 'Show'}
                </span>
            </button>

            {open && (
                <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
                    <div>
                        <p className="text-sm leading-7 text-slate-700">
                            {company.description || 'No company description is available yet.'}
                        </p>
                        {company.website && (
                            <a
                                href={company.website}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-4 inline-flex text-sm font-semibold text-[#2563eb] hover:text-[#1d4ed8]"
                            >
                                Visit website
                            </a>
                        )}
                    </div>

                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
                        {detailRows.map(([label, value]) => (
                            <div key={label} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</dt>
                                <dd className="mt-1 text-sm font-semibold text-slate-800">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )}
        </Panel>
    )
}

export const CompanyPage =() => {
    const[company, setCompany] = useState({})
    const[esgScore, setEsgScore] = useState({})
    const[riskData, setRiskData] = useState({})
    const[marketRiskData, setMarketRiskData] = useState(null)
    const[financialMetricsData, setFinancialMetricsData] = useState([])
    const[loading, setLoading] = useState(true)
    const[isFavorite, setIsFavorite] = useState(false)
    const[tab, setTab] = useState(0)
    let { symbol } = useParams(); 

    useEffect(() => {
        setTab(0)
    }, [symbol])

    useEffect(() => {
        setLoading(true)
        getOne("companies", {id: symbol}).then((response) => {
            setCompany(response.data.company)
            setEsgScore(response.data.esg_score)
            setRiskData(response.data.risk_data)
            setMarketRiskData(response.data.market_risk_data || null)
            setFinancialMetricsData(response.data.financial_metrics_data || [])
            localStorage.setItem('credits', response.data.usage_credits);
            setIsFavorite((response.data.company.favorite.length)>0 ? true : false)
            setLoading(false)
        }).catch(
            (e) => {
                toast.error("Error while loading companies", {})
                setLoading(false)
            }
        )
    },[symbol])
    
    const handleToggleFavorite = () => {
        create("watchlist", {data: {symbol: symbol}}).then((response) => {
            toast.success(response.data.message, {})
            setIsFavorite(!isFavorite)
        }).catch(
            (e) => {
                toast.error("Error while adding company to favorites", {})
            }
        )
    }

    if (company === null) {
        return <div>Company not found</div>
    }

    return (
      <div className="p-2 lg:p-8">
        <ToastContainer/>
        <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
                {!loading && <CompanyLogo company={company}/>}
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Company view</p>
                    <h1 className="truncate text-left text-2xl font-semibold text-slate-950">
                        {loading && <div className="h-8"></div>}
                        {!loading && <>{company.name} <span className="text-slate-500">({company.symbol})</span></>}
                    </h1>
                </div>
            </div>
          <button className="cursor-pointer rounded-md border border-slate-200 bg-white p-2 shadow-sm" onClick={handleToggleFavorite} type="button">{isFavorite ? <StarActive/> : <StarInactive/>}</button>
        </div>
        <Panel className="mb-4">
          <Tabs tabs={tabs} onSelection={setTab}/>
        </Panel>
        {!loading && <CompanyProfilePanel company={company}/>}
        {tab === 0 &&
            <OverviewTab 
                esgScore={esgScore}                
                company={company}
                loading={loading}
            />
        }
        {tab === 1 &&
        <EsgTab esgScore={esgScore} symbol={company.symbol}/>
        }
        {tab === 2 &&
        <FinanceTab company={company} financialMetricsData={financialMetricsData}/>
        }
        {tab === 3 &&
        <RiskTab 
            risk1={company.risk_corporate_score} 
            risk2={company.risk_market_score} 
            symbol={company.symbol}
            riskData={riskData}
            marketRiskData={marketRiskData}
        />
        }
        {tab === 4 &&
        <NewsTab company={company}/>
        }
        </div>
    );
}
